import {MipLevelRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {HalImage, HalImageRef, HalRef, PageDescriptor} from "@cm/hal"
import {Box2, Box2Like} from "@cm/math"
import {getMipLevelSize} from "@cm/hal/hal-image/utils"
import {ImageOpHalOutputImage} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-output-image"

export type Callback = {
    lockPages: (pageDescriptors: PageDescriptor[]) => void
    unlockPages: (pageDescriptors: PageDescriptor[]) => void
}

export class RegionLockedHalImageRef {
    static fromHalImageVirtualRef(image: ImageOpHalOutputImage, mipLevelRegion: MipLevelRegion, pageDescriptors: PageDescriptor[], callback: Callback) {
        return new RegionLockedHalImageRef(image, image.outputImagePtr, mipLevelRegion, {callback, pageDescriptors})
    }

    static fromHalImagePhysicalRef(image: ImageOpHalOutputImage, mipLevelRegion: MipLevelRegion) {
        return new RegionLockedHalImageRef(image, image.outputImagePtr, mipLevelRegion, undefined)
    }

    // promotes regionLockedHalImageRef to a view; don't touch regionLockedHalImageRef after this call !
    static makeView(regionLockedHalImageRef: RegionLockedHalImageRef, region?: Box2Like, mipLevelOffset?: number) {
        region ??= {
            x: 0,
            y: 0,
            ...getMipLevelSize(regionLockedHalImageRef.halImage.descriptor, regionLockedHalImageRef.mipLevelRegion.mipLevel),
        }
        mipLevelOffset ??= 0
        const regionIntersection = Box2.intersect(region, regionLockedHalImageRef.mipLevelRegion)
        if (regionLockedHalImageRef.virtualPages) {
            regionLockedHalImageRef.virtualPages.callback.lockPages(regionLockedHalImageRef.virtualPages.pageDescriptors) // lock pages for the new view // TODO restrict to the region !
        }
        const halImageView = regionLockedHalImageRef.halImage.createView({
            type: "sub-image",
            region,
            mipLevelOffset,
        })
        const view = new HalRef<HalImage>(halImageView, () => regionLockedHalImageRef.release())
        const regionLockedView = new RegionLockedHalImageRef(
            regionLockedHalImageRef.outputImage,
            view,
            {
                ...regionIntersection,
                mipLevel: regionLockedHalImageRef.mipLevelRegion.mipLevel + mipLevelOffset,
            },
            regionLockedHalImageRef.virtualPages,
        )
        view.release()
        return regionLockedView
    }

    private constructor(
        outputImage: ImageOpHalOutputImage,
        halImageRef: HalImageRef,
        readonly mipLevelRegion: MipLevelRegion,
        private virtualPages?: {
            callback: Callback
            pageDescriptors: PageDescriptor[]
        },
    ) {
        this._outputImage = outputImage
        this._outputImage.addRef("RegionLockedHalImageRef")
        this._halImageRef = halImageRef
        this._halImageRef.addRef("RegionLockedHalImageRef")
    }

    release() {
        if (!this._outputImage || !this._halImageRef) {
            throw new Error("Attempting to release a HalImageVirtualRef that has already been released")
        }
        this.virtualPages?.callback.unlockPages(this.virtualPages.pageDescriptors)
        this.virtualPages = undefined
        this._outputImage.release("RegionLockedHalImageRef")
        this._outputImage = null
        this._halImageRef.release("RegionLockedHalImageRef")
        this._halImageRef = null
    }

    get outputImage(): ImageOpHalOutputImage {
        if (!this._outputImage) {
            throw new Error("HalImageVirtualRef is disposed")
        }
        return this._outputImage
    }

    get halImageRef(): HalImageRef {
        if (!this._halImageRef) {
            throw new Error("HalImageVirtualRef is disposed")
        }
        return this._halImageRef
    }

    get halImage(): HalImage {
        return this.halImageRef.ref
    }

    private _outputImage: ImageOpHalOutputImage | null
    private _halImageRef: HalImageRef | null
}
